import React, {useState} from "react";
import {formatDateForDisplay, getDateRangeForAPI} from "./action/date-range-filter.action";
import {DateRangeState, DateRangeType, IDateRangeFilter, IMenuItems} from "./type/date-range-filter.type";
import {CheckOutlined} from "@ant-design/icons";
import {DatePicker} from "antd";
import Locale from "../../../core/locale/locale";
import {Dayjs} from "dayjs";
import "../../../styles/component/common/date-range-filter.scss";

// Icons
const DownIcon = () => (
    <svg className="down-icon" viewBox="0 0 1024 1024" fill="currentColor">
        <path
            d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"/>
    </svg>
);

const menuItems: IMenuItems[] = [
    // {key: '1', value: 'All', label: 'All'},
    {key: '2', value: 'Today', label: 'Today'},
    {key: '3', value: 'Yesterday', label: 'Yesterday'},
    {key: '4', value: 'Last 7 days', label: 'Last 7 days'},
    {key: '5', value: 'Last 30 days', label: 'Last 30 days'},
    {key: '6', value: 'Last 60 days', label: 'Last 60 days'},
    {key: '7', value: 'This Month', label: 'This Month'},
    {key: '8', value: 'custom', label: 'Custom range...', isCustom: true},
];

const DateRangeFilter: React.FC<IDateRangeFilter> = (props) => {
    const [dateRange, setDateRange] = useState<DateRangeState>({
        type: props.startDate ? "custom" : props.defaultSelection ?? "",
        startDate: '',
        endDate: ''
    });
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [showDatePicker, setShowDatePicker] = useState<boolean>(false);


    const [tempDates, setTempDates] = useState<{
        startDate: Dayjs | null;
        endDate: Dayjs | null;
    }>({
        startDate: null,
        endDate: null,
    });


    const handleMenuClick = (option: DateRangeType) => {
        if (option === 'custom') {
            setShowDatePicker(true);
            setDropdownOpen(false);
            setDateRange({type: option, startDate: '', endDate: ''});

        } else {
            const dateRange = getDateRangeForAPI(option);
            setDateRange({type: option, ...dateRange});
            setDropdownOpen(false);
            setShowDatePicker(false);
            props.onChange?.({type: option, ...dateRange});
        }
    };


    const handleApplyCustomRange = () => {
        if (tempDates.startDate && tempDates.endDate) {
            const start = tempDates.startDate.format(Locale.dateFormat);
            const end = tempDates.endDate.format(Locale.dateFormat);
            setDateRange({
                type: 'custom',
                startDate: start,
                endDate: end,
            });
            setShowDatePicker(false);
            setTempDates({startDate: null, endDate: null});
            props.onChange?.({type: 'custom', startDate: start, endDate: end});

        }
    };

    const handleCancel = () => {
        setShowDatePicker(false);
        setTempDates({startDate: null, endDate: null});
    };

    const isApplyDisabled = !tempDates.startDate || !tempDates.endDate || tempDates.startDate > tempDates.endDate;

    const getDisplayValue = () => {
        if (!dateRange.type) {
            return "Select date range";
        }

        if (
            dateRange.type === "custom" &&
            dateRange.startDate &&
            dateRange.endDate
        ) {
            return `${formatDateForDisplay(dateRange.startDate)} - ${formatDateForDisplay(dateRange.endDate)}`;
        }

        return dateRange.type;
    };


    return (
        <div className="date-filter-wrapper">
            <div className="date-range-filter">
                <div className="filter-controls">
                    <button
                        className={`filter-button ${dropdownOpen ? "open" : ""}`}
                        onClick={() => {
                            setDropdownOpen(!dropdownOpen);
                            setShowDatePicker(false);
                        }}
                    >
                        <span>{getDisplayValue()}</span>
                        <DownIcon/>
                    </button>
                </div>

                {dropdownOpen && (
                    <>
                        <div
                            className="dropdown-overlay"
                            onClick={() => setDropdownOpen(false)}
                        />
                        <div className="date-range-dropdown">
                            {menuItems.map((item: IMenuItems) => {

                                const isSelected =
                                    dateRange.type === item.label ||
                                    (dateRange.type === 'custom' && item.isCustom);

                                return (
                                    <div
                                        key={item.key}
                                        className={`menu-item ${isSelected ? "selected" : ""}`}
                                        onClick={() => handleMenuClick(item.value as DateRangeType)}
                                    >
                                        {isSelected && <CheckOutlined/>}
                                        <span className={isSelected ? "with-check" : ""}>{item.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {showDatePicker && (
                    <>
                        <div className="dropdown-overlay" onClick={handleCancel}/>
                        <div className="date-picker-container">
                            <div className="date-inputs">
                                <div className="date-input-group">
                                    <label className="date-label">Start Date</label>
                                    <DatePicker
                                        className="date-input"
                                        placeholder="Start Date"
                                        format={Locale.displayDate}
                                        value={tempDates.startDate}
                                        onChange={(value) =>
                                            setTempDates(prev => ({...prev, startDate: value}))
                                        }
                                    />
                                </div>

                                <div className="date-separator">—</div>

                                <div className="date-input-group">
                                    <label className="date-label">End Date</label>
                                    <DatePicker
                                        className="date-input"
                                        placeholder="End Date"
                                        format={Locale.displayDate}
                                        value={tempDates.endDate}
                                        onChange={(value) => {
                                            setTempDates(prev => ({...prev, endDate: value}))
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="date-picker-actions">
                                <button
                                    className="apply-button"
                                    onClick={handleApplyCustomRange}
                                    disabled={isApplyDisabled}
                                >
                                    Apply
                                </button>
                                <button className="cancel-button" onClick={handleCancel}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DateRangeFilter;
